import {
  InteractionRequiredAuthError,
  type AccountInfo,
  EventType,
} from "@azure/msal-browser";
import {
  MsalProvider,
  useIsAuthenticated as msalUseIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { loginRequest, pca } from "./msalConfig";

// MSAL Browser v3's ``initialize`` is async and **not idempotent** — calling
// it twice (e.g. React 18 Strict Mode's double-mount in dev) raises
// ``initialization_in_progress``. Cache the bootstrap promise at module scope
// so every effect awaits the same handshake regardless of how many times the
// provider mounts.
let _bootstrapPromise: Promise<void> | null = null;

function bootstrapMsal(): Promise<void> {
  if (_bootstrapPromise === null) {
    _bootstrapPromise = pca.initialize().then(() => {
      // Resolve any in-flight redirect from the auth provider before we hand
      // control back to the app shell.
      void pca.handleRedirectPromise();
    });
  }
  return _bootstrapPromise;
}

/**
 * MSAL provider wrapper plus the small set of hooks the rest of the app uses.
 * Silent token acquire with a redirect fallback when interaction is required.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let cancelled = false;
    bootstrapMsal().finally(() => {
      if (!cancelled) setInitialized(true);
    });

    const callbackId = pca.addEventCallback((message) => {
      if (
        message.eventType === EventType.LOGIN_SUCCESS &&
        message.payload &&
        "account" in message.payload
      ) {
        pca.setActiveAccount((message.payload as { account: AccountInfo }).account);
      }
    });

    return () => {
      cancelled = true;
      if (callbackId) pca.removeEventCallback(callbackId);
    };
  }, []);

  if (!initialized) {
    // Bare-bones loading state; the app shell shows a richer spinner once we
    // know whether the user is authenticated.
    return null;
  }

  return <MsalProvider instance={pca}>{children}</MsalProvider>;
}

export function useIsAuthenticated(): boolean {
  return msalUseIsAuthenticated();
}

export function useAuthLoading(): boolean {
  const { inProgress } = useMsal();
  // 'startup' means the SDK hasn't finished its initial check yet.
  return inProgress === "startup" || inProgress === "handleRedirect";
}

/**
 * Acquire a bearer token for the Northstar backend. Silent first; falls back
 * to a redirect when MSAL says interaction is required (refresh token expired,
 * conditional-access prompt, etc.).
 */
export function useAuthToken() {
  const { instance, accounts } = useMsal();

  return useCallback(async (): Promise<string | null> => {
    const account = instance.getActiveAccount() ?? accounts[0] ?? null;
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(loginRequest);
        return null;
      }
      throw err;
    }
  }, [instance, accounts]);
}

export function useLogin() {
  const { instance } = useMsal();
  return useCallback(() => {
    void instance.loginRedirect(loginRequest);
  }, [instance]);
}

export function useLogout() {
  const { instance } = useMsal();
  return useCallback(() => {
    void instance.logoutRedirect();
  }, [instance]);
}

export function useUsername(): string | null {
  const { accounts } = useMsal();
  const account = accounts[0];
  return account?.username ?? null;
}
